import { Box, Typography } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { useRef, useState } from "react";

import PropTypes from "prop-types";
import { Terminal } from "@mui/icons-material";
import { formatInputValuesToArgs } from "../utils/format";

/**
 * Expandable terminal-style display of the CLI args that would be passed to
 * the job's main command, derived live from the current form values via
 * formatInputValuesToArgs.
 *
 * @param {string} jobName
 * @param {Object} moduleVersionInputs - moduleVersion.definition.inputs
 */
export function JobNodeArgsDisplay({ jobName, moduleVersionInputs }) {
    const { control } = useFormContext();
    const inputValues = useWatch({ control, name: `${jobName}.main` });

    const [isExpanded, setIsExpanded] = useState(true);

    // Preserve the last successfully formatted string so transient invalid
    // form states (e.g. a positional cleared mid-edit) don't blank the box.
    // When stale, render in red to signal the displayed string no longer
    // reflects the current form values.
    const lastGoodArgsRef = useRef("");
    let argsString = lastGoodArgsRef.current;
    let isStale = true;
    try {
        argsString = formatInputValuesToArgs(
            moduleVersionInputs,
            inputValues || {}
        ).join(" ");
        lastGoodArgsRef.current = argsString;
        isStale = false;
    } catch {
        // keep previous string while form is transiently invalid
    }

    return (
        <Box
            onClick={() => setIsExpanded((v) => !v)}
            sx={{
                backgroundColor: isExpanded ? "#333" : "#555",
                p: 0.8,
                borderBottomLeftRadius: 8,
                borderBottomRightRadius: 8,
                cursor: "pointer",
                transition: "background-color 0.2s",
                "&:hover": { backgroundColor: "#444" },
            }}
        >
            {isExpanded ? (
                <Typography
                    variant="caption"
                    sx={{
                        fontFamily: "Consolas, monospace",
                        fontSize: "0.65rem",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        color: isStale ? "#ff8a80" : "#fff",
                        display: "block",
                    }}
                >
                    {argsString || " "}
                </Typography>
            ) : (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={0.5}
                >
                    <Terminal sx={{ fontSize: "1rem", color: "#fff" }} />
                    <Typography
                        variant="caption"
                        sx={{
                            fontFamily: "Consolas, monospace",
                            fontSize: "0.65rem",
                            color: "#fff",
                        }}
                    >
                        args
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

JobNodeArgsDisplay.propTypes = {
    jobName: PropTypes.string.isRequired,
    moduleVersionInputs: PropTypes.object.isRequired,
};
